import {
    FilterExpression,
    UnaryFilterOperator,
    MultipleValueFilterOperator,
    BinaryFilterOperator,
    FeedbackMessageFilterOperator,
    ActionsFilterOperator
} from "../Client/FilterExpression"
import { FilterAndSortExpression } from "../Client/FilterAndSortExpression"
import { Result } from "../Utility/Result"
import {
    assertValidDate,
    takeLast,
    takeFirst
} from "../../Helpers/Functions/dateFormat"
import { SortExpression } from "../Client/SortExpression"
import { APIAppTemplateModel, Protocol, Visibility } from "./AppTemplateModel"
import {
    APIFeedbackMessage,
    APIFeedbackTag,
    JobFeedbackModel
} from "./JobFeedbackModel"
import { asError } from "../../Helpers/Functions/error"
import { getAppSchema } from "../Client/Schema"
import { APIAppInstanceModel } from "./AppInstanceModel"

export type JobDialog =
    | "relabel"
    | "cancel"
    | "delete"
    | "enqueue"
    | "download"
    | undefined

export enum JobSortBy {
    id = "id",
    uuid = "uuid",
    created = "created",
    completed = "completed",
    status = "status",
    fmcreated = "fmcreated",
    score = "score",
    num_pages = "num_pages"
}

export enum JobOrderBy {
    asc = "asc",
    desc = "desc"
}

export enum JobHistoryOrderBy {
    asc = "asc",
    desc = "desc"
}

export enum JobHistorySortBy {
    version = "version",
    completed = "completed",
    status = "status",
    correct = "correct"
}

export interface JobHistoryQueryParams {
    orderBy: JobHistoryOrderBy
    page: number
    size: number
    sortBy: JobHistorySortBy
}

export enum JobStatus {
    SCHEDULED = "SCHEDULED",
    IN_PROGRESS = "IN_PROGRESS",
    FAILED = "FAILED",
    SUSPENDED = "SUSPENDED",
    CANCELED = "CANCELED",
    EXPIRED = "EXPIRED",
    COMPLETED = "COMPLETED",
    PENDING = "PENDING",
    ENQUEUED = "ENQUEUED",
    INTERNAL_ERROR = "INTERNAL_ERROR",
    IN_CREATION = "IN_CREATION"
}

export function jobStatusToName(
    status: JobStatus,
    isLabelByCollaborator: boolean
): string {
    switch (status) {
        case JobStatus.CANCELED:
            return "Canceled"
        case JobStatus.COMPLETED:
            return "Completed"
        case JobStatus.EXPIRED:
            return "Expired"
        case JobStatus.FAILED:
            return "Failed"
        case JobStatus.IN_PROGRESS:
        case JobStatus.INTERNAL_ERROR:
            return isLabelByCollaborator ? "Ready to process" : "Processing"
        case JobStatus.PENDING:
            return "Parked"
        case JobStatus.SCHEDULED:
        case JobStatus.ENQUEUED:
            return "Queued"
        case JobStatus.SUSPENDED:
            return "Paused"
        case JobStatus.IN_CREATION:
            return "Creating"
    }
}

export enum JobType {
    CALIBRATION = "CALIBRATION",
    ONBOARDING = "ONBOARDING",
    DEFAULT = "DEFAULT",
    COLLABORATOR = "COLLABORATOR"
}

export type JobReviewStatus = "CORRECT" | "NOT_CORRECT" | "NOT_REVIEWED"

export interface APIJobModel {
    appId: string
    callbackUrl: string | null
    completed: string
    created: string
    id: string
    uuid: string
    input: unknown
    metadata: { [key: string]: unknown } | null
    status: JobStatus
    correct: boolean | null
    jobType: JobType
    tags: string[] | null
    score: number | null
    internalMetadata: { [key: string]: any } | null
    isBigResponse: boolean | null
    workers: {
        ais: string[] | null
        users: string[] | null
        crowd: boolean | null
    } | null
}

export interface JobVersionItem {
    jobID: string // original ID on creation of job
    versionJobId: string // changes when a job is relabeled
    originalJobId: string // alias to jobID
    value: number
    versionName: string
    correct: boolean | null
    status: JobStatus
    created: Date | null
    isLatestVersion: boolean
    response: unknown
    responseData: unknown
    label: JSX.Element
}

export interface APIJobQueryModel {
    completed: string
    created: string
    id: string
    uuid: string
    input: JsonFilter
    response: JsonFilter
    onlyIfFeedback: boolean
    feedbackSearch: string
    feedbackTags: number | null
    fmcreated: string | null
    messages: FeedbackMessageFilter
    batchId: string
    actions: ActionsFilter
    metadata: { [key: string]: unknown } | null
    status: JobStatus
    correct: boolean | null
    tags: string
    score: number | null
    num_pages: number | null
}

export type JsonFilterMatchType =
    | "IS"
    | "IS_NOT"
    | "CONTAINS"
    | "CONTAINS_NOT"
    | "IS_NOT_EMPTY"

export interface JsonFilter {
    path: (string | number)[]
    matchType: JsonFilterMatchType
    value: string | number | boolean
}

export interface FeedbackMessageFilter {
    onlyIfFeedback?: boolean
    feedbackSearch?: string
    feedbackTags?: number
}

export interface ActionsFilter {
    firstIncorrect?: boolean
    relabeled?: boolean
    edited?: boolean
}

export const JobHistoryQueryParams = {
    orderBy: JobHistoryOrderBy.desc,
    sortBy: JobHistorySortBy.version
}

export function getJobID(model: APIJobModel): string {
    return model.id
}

export interface APIJobCreateResult {
    uuid: string
    batch_id: string
    message: string
    truncated_inputs: boolean
}

export interface JobResponseModel {
    response: unknown
    responseData: unknown
}

export interface JobHistoryModel {
    history: JobVersionModel[]
    total: number
    pageSize: number
    pageNumber: number
    pages: number
}

export interface JobVersionModel {
    version: string
    relabelId: number
    completed: string
    status: JobStatus
    correct: boolean
    feedback: JobFeedbackModel | null
    jobId: number | null
    created: string | null
    jobCreated: string | null
    jobCreatedEdited: string | null
    versionJobId: number | null
}

export interface JobHistoryVersionResponseModel {
    response: unknown
    responseData: unknown
}

export enum FeedbackMessageSortBy {
    state = "state",
    created = "created"
}

export enum FeedbackMessageOrderBy {
    asc = "asc",
    desc = "desc"
}

export interface FeedbackMessageListParams {
    orderBy: FeedbackMessageOrderBy
    sortBy: FeedbackMessageSortBy
}

export interface APIJobReviewResult {
    baselineId: number
    correct: boolean
    response: unknown
    responseData: unknown
    operationId?: number
}

export interface APIJobFeedbackMessageModel extends APIJobModel {
    feedbackTags?: APIFeedbackTag[]
    feedbackMessage?: APIFeedbackMessage
}

export interface RelabelJobResponse {
    message: string
    uuid: string
    batch_id: string
}

export interface JobIdsOfBatchResponse {
    job_ids: string[]
}

interface APIJobParamsBase {
    idGreaterThan?: number
    idLessThan?: number
    createdStartDate?: string
    createdEndDate?: string
    statusIn?: JobStatus[]
    tags?: string[]
    notTags?: string[]
    inputPath?: string
    inputMatchType?: JsonFilterMatchType
    inputValue?: string
    metadataSearch?: string
    scoreGreaterThan?: number
    scoreLessThan?: number
    batchId?: string
    firstIncorrect?: boolean
    relabeled?: boolean
    edited?: boolean
}

interface APICompletedJobParams {
    completedStartDate?: string
    completedEndDate?: string
    reviewed?: JobReviewStatus[]
    responsePath?: string
    responseMatchType?: JsonFilterMatchType
    responseValue?: string
}

interface APIJobSortParams {
    sortBy: JobSortBy
    orderBy: JobOrderBy
}

export interface APIJobHistoryParams {
    sortBy: JobHistorySortBy
    orderBy: JobHistoryOrderBy
}

interface APIJobFeedbackMessageParams {
    onlyIfFeedback?: boolean
    feedbackSearch?: string
    feedbackTags?: number
}

interface APIJobAdditionalFeedbackMessageParams {
    feedbackMessageCreatedBefore?: string
}

export type APIJobListParams = APIJobParamsBase &
    APICompletedJobParams &
    APIJobSortParams &
    APIJobFeedbackMessageParams &
    APIJobAdditionalFeedbackMessageParams

export type APIJobCountByStatusParams = APIJobParamsBase &
    APICompletedJobParams &
    APIJobFeedbackMessageParams

export type APIJobRelabelParams = APIJobParamsBase &
    APICompletedJobParams &
    APIJobFeedbackMessageParams

export type APIJobCancelParams = APIJobParamsBase & APIJobFeedbackMessageParams

export type APIJobEnqueueParams = Omit<APIJobParamsBase, "statusIn"> &
    APIJobFeedbackMessageParams

export interface JobIDReviewModel {
    jobId: string
    total: number
}

export const defaultParams: APIJobListParams = {
    orderBy: JobOrderBy.desc,
    sortBy: JobSortBy.created
}

export const defaultJobMessagesParams: APIJobListParams = {
    orderBy: JobOrderBy.desc,
    sortBy: JobSortBy.fmcreated
}

export const defaultJobSortExpression: SortExpression<APIJobQueryModel> = {
    sortBy: defaultParams.sortBy,
    desc: defaultParams.orderBy === JobOrderBy.desc
}

export const defaultJobMessageSortExpression: SortExpression<APIJobQueryModel> =
    {
        sortBy: JobSortBy.fmcreated,
        desc: true
    }

export const getJobSortExpression = (
    sort?: SortExpression<APIJobQueryModel>
): SortExpression<APIJobQueryModel> =>
    sort === undefined || sort.sortBy === "fmcreated"
        ? defaultJobSortExpression
        : sort

export interface JobsDownload {
    link?: string
    expires?: number
    ref: string
}

export function isValid(download: JobsDownload): download is {
    link: string
    expires?: number
    ref: string
} {
    return (
        download.link !== undefined &&
        (download.expires === undefined || download.expires > Date.now())
    )
}

export function isProcessing(job: APIJobModel): boolean {
    return (
        job.status === JobStatus.IN_PROGRESS ||
        job.status === JobStatus.SCHEDULED ||
        job.status === JobStatus.ENQUEUED
    )
}

export function isToBeReviewed(job: APIJobModel): boolean {
    return job.status === JobStatus.COMPLETED && job.correct === null
}

export function decodeJobListParams({
    filter,
    sort
}: FilterAndSortExpression<APIJobQueryModel>): Result<APIJobListParams> {
    const sortBy =
        sort.sortBy === "uuid"
            ? JobSortBy.uuid
            : sort.sortBy === "id"
            ? JobSortBy.id
            : sort.sortBy === "num_pages"
            ? JobSortBy.num_pages
            : sort.sortBy === "created"
            ? JobSortBy.created
            : sort.sortBy === "status"
            ? JobSortBy.status
            : sort.sortBy === "fmcreated"
            ? JobSortBy.fmcreated
            : sort.sortBy === "score"
            ? JobSortBy.score
            : undefined

    function traverse(
        passed: APIJobListParams,
        f: FilterExpression<APIJobQueryModel>
    ): APIJobListParams {
        switch (f.fieldName) {
            case "id":
                switch (f.operator) {
                    case UnaryFilterOperator.le: {
                        const value = parseInt(f.value, 10)

                        return {
                            ...passed,
                            idLessThan:
                                !isNaN(value) &&
                                (passed.idLessThan === undefined ||
                                    passed.idLessThan > value)
                                    ? value
                                    : passed.idLessThan
                        }
                    }
                    case UnaryFilterOperator.ge: {
                        const value = parseInt(f.value, 10)

                        return {
                            ...passed,
                            idGreaterThan:
                                !isNaN(value) &&
                                (passed.idGreaterThan === undefined ||
                                    passed.idGreaterThan < value)
                                    ? value
                                    : passed.idGreaterThan
                        }
                    }
                    case BinaryFilterOperator.between: {
                        const lowerValue = parseInt(f.value[0], 10)
                        const higherValue = parseInt(f.value[1], 10)

                        return {
                            ...passed,
                            idGreaterThan:
                                !isNaN(lowerValue) &&
                                (passed.idGreaterThan === undefined ||
                                    passed.idGreaterThan < lowerValue)
                                    ? lowerValue
                                    : passed.idGreaterThan,
                            idLessThan:
                                !isNaN(higherValue) &&
                                (passed.idLessThan === undefined ||
                                    passed.idLessThan > higherValue)
                                    ? higherValue
                                    : passed.idLessThan
                        }
                    }
                    default:
                        throw Error("Unsupported operator")
                }
            case "created":
            case "completed":
                const startField =
                    f.fieldName === "completed"
                        ? "completedStartDate"
                        : "createdStartDate"
                const endField =
                    f.fieldName === "completed"
                        ? "completedEndDate"
                        : "createdEndDate"
                const passedStart = passed[startField]
                const passedEnd = passed[endField]
                let updated = passed

                switch (f.operator) {
                    case UnaryFilterOperator.ge: {
                        assertValidDate(f.value)

                        updated = {
                            ...passed,
                            [startField]:
                                passedStart === undefined
                                    ? f.value
                                    : takeLast(f.value, passedStart)
                        }
                        break
                    }
                    case UnaryFilterOperator.le: {
                        assertValidDate(f.value)

                        updated = {
                            ...passed,
                            [endField]:
                                passedEnd === undefined
                                    ? f.value
                                    : takeFirst(f.value, passedEnd)
                        }
                        break
                    }
                    default:
                        throw Error("Unsupported operator")
                }

                if (f.fieldName === "completed") {
                    return { ...updated, statusIn: [JobStatus.COMPLETED] }
                }
                return updated
            case "status":
                switch (f.operator) {
                    case UnaryFilterOperator.eq:
                        return {
                            ...passed,
                            statusIn: [f.value]
                        }
                    case MultipleValueFilterOperator.isOneOf:
                        return f.value.length === 0
                            ? passed
                            : { ...passed, statusIn: f.value }
                    default:
                        throw Error("Unsupported operator")
                }
            case "correct":
                switch (f.operator) {
                    case UnaryFilterOperator.eq:
                        return {
                            ...passed,
                            reviewed: [correctToReviewStatus(f.value)]
                        }
                    case MultipleValueFilterOperator.isOneOf: {
                        const reviewed = f.value.map(correctToReviewStatus)

                        return reviewed.length === 0
                            ? passed
                            : { ...passed, reviewed }
                    }
                    default:
                        throw Error("Unsupported operator")
                }
            case "tags":
                switch (f.operator) {
                    case MultipleValueFilterOperator.isOneOf:
                        return f.value.length === 0
                            ? passed
                            : { ...passed, tags: f.value }
                    case MultipleValueFilterOperator.isNotOneOf:
                        return f.value.length === 0
                            ? passed
                            : { ...passed, notTags: f.value }
                    default:
                        throw Error("Unsupported operator")
                }
            case "input":
                switch (f.operator) {
                    case UnaryFilterOperator.contains:
                        return {
                            ...passed,
                            ...jsonFilterToPartialParams(f.value, "input")
                        }
                    default:
                        throw Error("Unsupported operator")
                }
            case "response":
                switch (f.operator) {
                    case UnaryFilterOperator.contains:
                        return {
                            ...passed,
                            ...jsonFilterToPartialParams(f.value, "response")
                        }
                    default:
                        throw Error("Unsupported operator")
                }
            case "metadata":
                switch (f.operator) {
                    case UnaryFilterOperator.contains:
                        return f.value !== null &&
                            "search" in f.value &&
                            typeof f.value.search === "string" &&
                            f.value.search !== ""
                            ? { ...passed, metadataSearch: f.value.search }
                            : passed

                    default:
                        throw Error("Unsupported operator")
                }

            case "messages":
                switch (f.operator) {
                    case FeedbackMessageFilterOperator.isMessage:
                    case FeedbackMessageFilterOperator.isMessageContains:
                    case FeedbackMessageFilterOperator.isOneOfTag:
                        return { ...passed, ...f.value }

                    default:
                        throw Error("Unsupported filtering field")
                }
            case "score":
                switch (f.operator) {
                    case UnaryFilterOperator.le: {
                        const value = f.value

                        return {
                            ...passed,
                            scoreLessThan:
                                value !== null &&
                                !isNaN(value) &&
                                (passed.scoreLessThan === undefined ||
                                    passed.scoreLessThan > value)
                                    ? value
                                    : passed.scoreLessThan
                        }
                    }
                    case UnaryFilterOperator.ge: {
                        const value = f.value

                        return {
                            ...passed,
                            scoreGreaterThan:
                                value !== null &&
                                !isNaN(value) &&
                                (passed.scoreGreaterThan === undefined ||
                                    passed.scoreGreaterThan < value)
                                    ? value
                                    : passed.scoreGreaterThan
                        }
                    }
                    case BinaryFilterOperator.between: {
                        const lowerValue = f.value[0]
                        const higherValue = f.value[1]

                        return {
                            ...passed,
                            scoreGreaterThan:
                                lowerValue !== null &&
                                !isNaN(lowerValue) &&
                                (passed.scoreGreaterThan === undefined ||
                                    passed.scoreGreaterThan < lowerValue)
                                    ? lowerValue
                                    : passed.scoreGreaterThan,
                            scoreLessThan:
                                higherValue !== null &&
                                !isNaN(higherValue) &&
                                (passed.scoreLessThan === undefined ||
                                    passed.scoreLessThan > higherValue)
                                    ? higherValue
                                    : passed.scoreLessThan
                        }
                    }
                    default:
                        throw Error("Unsupported operator")
                }
            case "batchId":
                switch (f.operator) {
                    case UnaryFilterOperator.eq:
                        return { ...passed, batchId: f.value }
                    default:
                        throw Error("Unsupported operator")
                }
            case "actions":
                switch (f.operator) {
                    case ActionsFilterOperator.firstIncorrect:
                        return {
                            ...passed,
                            firstIncorrect: f.value.firstIncorrect
                        }
                    case ActionsFilterOperator.relabeled:
                        return {
                            ...passed,
                            relabeled: f.value.relabeled
                        }
                    case ActionsFilterOperator.edited:
                        return { ...passed, edited: f.value.edited }
                    default:
                        throw Error("Unsupported operator")
                }
            default:
                throw Error("Unsupported filtering field")
        }
    }

    if (sortBy === undefined) {
        return Result.error("Unsupported sort field")
    }

    try {
        return Result.ok(
            filter.reduce(traverse, {
                sortBy,
                orderBy: sort.desc ? JobOrderBy.desc : JobOrderBy.asc
            })
        )
    } catch (e) {
        return Result.error(asError(e).message)
    }
}

export function notEnqueuable() {
    return [
        JobStatus.COMPLETED,
        JobStatus.FAILED,
        JobStatus.EXPIRED,
        JobStatus.CANCELED,
        JobStatus.SCHEDULED,
        JobStatus.ENQUEUED,
        JobStatus.IN_PROGRESS,
        JobStatus.SUSPENDED
    ]
}

export function notRelabelable() {
    return [
        JobStatus.PENDING,
        JobStatus.SCHEDULED,
        JobStatus.ENQUEUED,
        JobStatus.IN_PROGRESS,
        JobStatus.SUSPENDED
    ]
}

export function relabelable() {
    return [
        JobStatus.COMPLETED,
        JobStatus.FAILED,
        JobStatus.EXPIRED,
        JobStatus.CANCELED
    ]
}

export function cancelable() {
    return [
        JobStatus.PENDING,
        JobStatus.SCHEDULED,
        JobStatus.ENQUEUED,
        JobStatus.IN_PROGRESS,
        JobStatus.SUSPENDED
    ]
}

export function notCancelable() {
    return [
        JobStatus.COMPLETED,
        JobStatus.FAILED,
        JobStatus.EXPIRED,
        JobStatus.CANCELED
    ]
}

export function deletable() {
    return [
        JobStatus.PENDING,
        JobStatus.SCHEDULED,
        JobStatus.IN_PROGRESS,
        JobStatus.SUSPENDED,
        JobStatus.COMPLETED,
        JobStatus.FAILED,
        JobStatus.EXPIRED,
        JobStatus.CANCELED
    ]
}

export function notDeletable() {
    return []
}

export function enqueuable() {
    return [JobStatus.PENDING]
}

export function hasNoTasks(
    protocol: Protocol,
    visibility: Visibility,
    jobType: JobType
) {
    return (
        protocol === Protocol.WEBSOCKET &&
        visibility === "PUBLIC" &&
        jobType === JobType.COLLABORATOR
    )
}

export function labelable() {
    return [JobStatus.IN_PROGRESS, JobStatus.SCHEDULED]
}

export function unprocessable() {
    return [JobStatus.INTERNAL_ERROR, JobStatus.ENQUEUED]
}

export function terminalState() {
    return [
        JobStatus.INTERNAL_ERROR,
        JobStatus.CANCELED,
        JobStatus.COMPLETED,
        JobStatus.EXPIRED,
        JobStatus.FAILED
    ]
}

export function isCancelableJob(job: APIJobModel): boolean {
    return cancelable().includes(job.status)
}

export function isRelabelableJob(job: APIJobModel): boolean {
    return relabelable().includes(job.status)
}

export function isDeletableJob(job: APIJobModel): boolean {
    return deletable().includes(job.status)
}

export function isEnqueueableJob(job: APIJobModel): boolean {
    return enqueuable().includes(job.status)
}

export function isUnprocessable(job: APIJobModel): boolean {
    return unprocessable().includes(job.status)
}

export function isLabelableJob(job: APIJobModel): boolean {
    return labelable().includes(job.status)
}

export function correctToReviewStatus(
    correct: boolean | null
): JobReviewStatus {
    return correct === null
        ? "NOT_REVIEWED"
        : correct
        ? "CORRECT"
        : "NOT_CORRECT"
}

function encodeJobSortParams(
    { sortBy, orderBy }: APIJobListParams,
    baseSearch?: URLSearchParams
) {
    const search = new URLSearchParams(baseSearch)

    search.set("orderBy", orderBy)
    search.set("sortBy", sortBy)

    return search
}

function encodeJobParamsBase(
    {
        idGreaterThan,
        idLessThan,
        createdStartDate,
        createdEndDate,
        statusIn,
        tags,
        notTags,
        inputMatchType,
        inputPath,
        inputValue,
        metadataSearch,
        scoreGreaterThan,
        scoreLessThan,
        batchId,
        firstIncorrect,
        relabeled,
        edited
    }: APIJobParamsBase,
    baseSearch?: URLSearchParams,
    { excludeStatusIn }: { excludeStatusIn?: boolean } = {}
): URLSearchParams {
    const search = new URLSearchParams(baseSearch)

    if (idGreaterThan !== undefined) {
        search.set("idGreaterThan", String(idGreaterThan))
    }

    if (idLessThan !== undefined) {
        search.set("idLessThan", String(idLessThan))
    }

    if (createdStartDate !== undefined) {
        search.set("createdStartDate", createdStartDate)
    }

    if (createdEndDate !== undefined) {
        search.set("createdEndDate", createdEndDate)
    }

    if (statusIn !== undefined && excludeStatusIn !== true) {
        statusIn.forEach(s => search.append("statusIn", s))
    }

    if (tags !== undefined) {
        tags.forEach(s => search.append("tags", s))
    }

    if (notTags !== undefined) {
        notTags.forEach(s => search.append("notTags", s))
    }

    if (
        inputPath !== undefined &&
        inputMatchType !== undefined &&
        inputValue !== undefined
    ) {
        search.set("inputPath", inputPath)
        search.set("inputMatchType", inputMatchType)
        search.set("inputValue", inputValue)
    }

    if (metadataSearch !== undefined) {
        search.set("metadataSearch", metadataSearch)
    }

    if (scoreGreaterThan !== undefined) {
        search.set("scoreGreaterThan", String(scoreGreaterThan))
    }

    if (scoreLessThan !== undefined) {
        search.set("scoreLessThan", String(scoreLessThan))
    }

    if (batchId !== undefined) {
        search.set("batchId", batchId)
    }

    if (firstIncorrect !== undefined) {
        search.set("firstIncorrect", String(firstIncorrect))
    }

    if (relabeled !== undefined) {
        search.set("relabeled", String(relabeled))
    }

    if (edited !== undefined) {
        search.set("edited", String(edited))
    }

    return search
}

function encodeCompletedJobParams(
    {
        completedStartDate,
        completedEndDate,
        reviewed,
        responseMatchType,
        responsePath,
        responseValue
    }: APICompletedJobParams,
    baseSearch?: URLSearchParams
): URLSearchParams {
    const search = new URLSearchParams(baseSearch)

    if (completedStartDate !== undefined) {
        search.set("completedStartDate", completedStartDate)
    }

    if (completedEndDate !== undefined) {
        search.set("completedEndDate", completedEndDate)
    }

    if (reviewed !== undefined) {
        reviewed.forEach(s => search.append("reviewed", s))
    }

    if (
        responsePath !== undefined &&
        responseMatchType !== undefined &&
        responseValue !== undefined
    ) {
        search.set("responsePath", responsePath)
        search.set("responseMatchType", responseMatchType)
        search.set("responseValue", responseValue)
    }

    return search
}

function encodeJobFeedbackMessageParams(
    {
        onlyIfFeedback,
        feedbackSearch,
        feedbackTags
    }: APIJobFeedbackMessageParams,
    baseSearch?: URLSearchParams
): URLSearchParams {
    const search = new URLSearchParams(baseSearch)

    if (onlyIfFeedback !== undefined) {
        search.set("onlyIfFeedback", onlyIfFeedback.toString())
    }

    if (feedbackSearch !== undefined) {
        search.set("feedbackSearch", feedbackSearch.toString())
    }

    if (feedbackTags !== undefined) {
        search.append("feedbackTags", String(feedbackTags))
    }
    return search
}

function encodeJobAdditionalFeedbackMessageParams(
    { feedbackMessageCreatedBefore }: APIJobAdditionalFeedbackMessageParams,
    baseSearch?: URLSearchParams
): URLSearchParams {
    const search = new URLSearchParams(baseSearch)

    if (feedbackMessageCreatedBefore !== undefined) {
        search.set("feedbackMessageCreatedBefore", feedbackMessageCreatedBefore)
    }
    return search
}

export function encodeJobAPIListParams(
    params: APIJobListParams
): URLSearchParams {
    return encodeJobAdditionalFeedbackMessageParams(
        params,
        encodeJobSortParams(
            params,
            encodeJobFeedbackMessageParams(
                params,
                encodeCompletedJobParams(params, encodeJobParamsBase(params))
            )
        )
    )
}

export function encodeJobCancelAPIParams(
    params: APIJobCancelParams
): URLSearchParams {
    return encodeJobFeedbackMessageParams(params, encodeJobParamsBase(params))
}

export function encodeJobEnqueueAPIParams(
    params: APIJobEnqueueParams
): URLSearchParams {
    return encodeJobFeedbackMessageParams(
        params,
        encodeJobParamsBase(params, undefined, { excludeStatusIn: true })
    )
}

export function encodeJobRelabelAPIParams(
    params: APIJobRelabelParams
): URLSearchParams {
    return encodeJobFeedbackMessageParams(
        params,
        encodeCompletedJobParams(params, encodeJobParamsBase(params))
    )
}

export function encodeJobCountByStatusAPIParams(
    params: APIJobCountByStatusParams
): URLSearchParams {
    return encodeJobFeedbackMessageParams(
        params,
        encodeCompletedJobParams(params, encodeJobParamsBase(params))
    )
}

function jsonFilterToPartialParams(
    filter: JsonFilter,
    where: "input" | "response"
): Partial<APIJobListParams> {
    const value = filter.value.toString()
    const isEmptyMatchType = filter.matchType === "IS_NOT_EMPTY"

    if (!isEmptyMatchType && value === "") {
        return {}
    }

    if (where === "input") {
        return {
            inputPath: `{${filter.path.map(pe => String(pe)).join(".")}}`,
            inputMatchType: filter.matchType,
            inputValue: value
        }
    }

    return {
        responsePath: `{${filter.path.map(pe => String(pe)).join(".")}}`,
        responseMatchType: filter.matchType,
        responseValue: value
    }
}

export function matchedTypeToString(value: JsonFilterMatchType): string {
    switch (value) {
        case "CONTAINS":
            return "contains"
        case "CONTAINS_NOT":
            return "does not contain"
        case "IS":
            return "is"
        case "IS_NOT":
            return "is not"
        case "IS_NOT_EMPTY":
            return "is not empty"
    }
}

export const getDefaultFeedbackParams = () => ({
    orderBy: FeedbackMessageOrderBy.desc,
    sortBy: FeedbackMessageSortBy.created
})

export function isJobFeedbackModel(
    item: APIJobModel
): item is APIJobFeedbackMessageModel {
    return (item as APIJobFeedbackMessageModel).feedbackMessage !== undefined
}

export function isFiltered(params: APIJobListParams): boolean {
    return (Object.keys(params) as (keyof APIJobListParams)[]).some(
        param =>
            param !== "sortBy" &&
            param !== "orderBy" &&
            params[param] !== undefined
    )
}

export function getSchemaFormData(
    job: APIJobModel,
    jobResponse: JobResponseModel,
    instance: APIAppInstanceModel,
    template: APIAppTemplateModel
) {
    const schema = getAppSchema(instance, template, false)

    return {
        input: { ...schema.input, formData: job.input },
        output: { ...schema.output, formData: jobResponse.response }
    }
}

export function getSchemaFormattedFormData(
    job: APIJobModel,
    jobResponse: JobResponseModel,
    instance: APIAppInstanceModel,
    template: APIAppTemplateModel
) {
    return {
        input: {
            schema: instance.inputSchema ?? template.inputSchema,
            uiSchema: instance.inputUiSchema ?? template.inputUiSchema ?? {},
            formData: job.input
        },
        output: {
            schema: instance.outputSchema ?? template.outputSchema,
            uiSchema: instance.outputUiSchema ?? template.outputUiSchema ?? {},
            formData: jobResponse.response
        }
    }
}
